"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-12 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-12 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-12 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-12 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center justify-center px-4 py-2 text-xs font-mono uppercase tracking-widest text-foreground/80 hover:text-foreground hover:bg-transparent focus:bg-transparent transition-colors duration-200">
                <span className="relative">
                  {item.label}
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-1 w-1 rounded-lg bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
                boxShadow: "0 0 8px var(--primary)"
              }} />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 pt-12">
        <div className="h-12 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-3/5 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-8 pt-12">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="group flex items-baseline gap-4 py-3 border-b border-border/40 text-foreground hover:text-primary transition-colors duration-200">
          <span className="font-mono text-xs tracking-widest text-primary/80">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default text-3xl sm:text-4xl tracking-tight">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 20);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? scrollY / docHeight * 100 : 0;
      setProgress(Math.min(100, Math.max(0, scrollPercent)));
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3718" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/70 backdrop-blur-xl border-b border-border/40" : "border-b border-transparent"}`}>
      <Progress value={progress} className="absolute top-0 left-0 right-0 h-px w-full bg-transparent rounded-md [&>[data-slot=progress-indicator]]:bg-primary" style={{
      height: "2px"
    }} />

      <div className={`mx-auto max-w-7xl px-6 lg:px-8 transition-all duration-300 ${scrolled ? "py-4" : "py-6 lg:py-8"}`}>
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center">
            <Link to="/HomePage" className="inline-flex items-center" aria-label="Luxury Construction LLC home">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          <div className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex items-center">
            <Button asChild className="group relative h-10 bg-primary text-primary-foreground px-5 text-xs font-mono uppercase tracking-widest hover:bg-primary/90 transition-all duration-300" style={{
            boxShadow: "0 0 24px -4px var(--primary)"
          }}>
              <Link to="/ContactUsPage">
                <span>Get a Quote</span>
                <ArrowRight className="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          <div className="flex lg:hidden items-center gap-3">
            <Button asChild size="sm" className="hidden sm:inline-flex h-9 bg-primary text-primary-foreground px-4 text-xs font-mono uppercase tracking-widest hover:bg-primary/90">
              <Link to="/ContactUsPage">
                <span>Quote</span>
                <ArrowRight className="ml-1 size-3.5" />
              </Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/50" aria-label="Open menu">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background text-foreground border-l border-border/40 p-0">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>

                <div className="flex items-center justify-between px-6 py-6 border-b border-border/40">
                  <Link to="/HomePage" onClick={() => setMobileOpen(false)} className="inline-flex items-center" aria-label="Luxury Construction LLC home">
                    <WvcLogo className="h-7 w-auto" />
                  </Link>
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    Menu
                  </span>
                </div>

                <MenuProvider menu_id="16">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-auto px-8 pb-10">
                  <Button asChild className="group w-full h-12 bg-primary text-primary-foreground text-xs font-mono uppercase tracking-widest hover:bg-primary/90" style={{
                  boxShadow: "0 0 24px -4px var(--primary)"
                }}>
                    <Link to="/ContactUsPage" onClick={() => setMobileOpen(false)}>
                      <span>Get a Quote</span>
                      <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Luxury Construction LLC — Professional Roofing & Real Estate
                  </p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}